import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, Paper, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  BLOCK_TYPES,
  applyPersonalization,
  cloneBlock,
  createBlock,
  isEmptyRichHtml,
  renderCampaignBody,
} from "../../../../Utils/campaignBlocks";
import { buildClientEmailPreviewHtml } from "../../../../Utils/clientEmailPreview";
import BlockForm from "./BlockForm";

const TYPE_LABELS = {
  hero: "Hero",
  heading: "Title",
  greeting: "Greeting",
  text: "Text",
  section: "Section",
  facts: "Facts",
  button: "Button",
  image: "Image",
  divider: "Divider",
  note: "Footnote",
};

function blockSummary(block) {
  if (!block) return "";
  switch (block.type) {
    case "hero":
      return block.url ? "Image set" : "No image";
    case "heading":
      return block.title || "Untitled";
    case "greeting":
      return block.enabled === false ? "Off" : "Dear first name";
    case "text":
      return isEmptyRichHtml(block.html) ? "Empty" : "Paragraph";
    case "section":
      return block.title || "Untitled section";
    case "facts":
      return `${(block.items || []).length} items`;
    case "button":
      return block.label || "Button";
    case "image":
      return block.url ? block.caption || "Image set" : "No image";
    case "divider":
      return "Line";
    case "note":
      return isEmptyRichHtml(block.html) ? "Empty" : "Small print";
    default:
      return "";
  }
}

const BlockEditor = ({ blocks, onChange, eyebrow, previewName = "Client" }) => {
  const [selectedId, setSelectedId] = useState(blocks[0]?.id || null);
  const selected = blocks.find((b) => b.id === selectedId) || null;

  useEffect(() => {
    if (!blocks.some((b) => b.id === selectedId)) {
      setSelectedId(blocks[0]?.id || null);
    }
  }, [blocks, selectedId]);

  const liveHtml = useMemo(() => {
    const first = String(previewName || "Client").split(/\s+/)[0] || "Client";
    const body = applyPersonalization(renderCampaignBody(blocks), {
      full_name: previewName || "Client",
      first_name: first,
    });
    return buildClientEmailPreviewHtml(body, { eyebrow: eyebrow || "CLIENT UPDATE" });
  }, [blocks, eyebrow, previewName]);

  const updateAt = (id, nextBlock) => {
    onChange(blocks.map((b) => (b.id === id ? nextBlock : b)));
  };

  const move = (index, dir) => {
    const next = [...blocks];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  const remove = (id) => {
    const next = blocks.filter((b) => b.id !== id);
    onChange(next);
    if (selectedId === id) setSelectedId(next[0]?.id || null);
  };

  const add = (type) => {
    const block = createBlock(type);
    onChange([...blocks, block]);
    setSelectedId(block.id);
  };

  const duplicate = (block) => {
    const copy = cloneBlock(block);
    const idx = blocks.findIndex((b) => b.id === block.id);
    const next = [...blocks];
    next.splice(idx + 1, 0, copy);
    onChange(next);
    setSelectedId(copy.id);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 46%" }, minWidth: 0 }}>
        <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
          BLOCKS
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.5 }}>
          {blocks.map((block, index) => (
            <Paper
              key={block.id}
              onClick={() => setSelectedId(block.id)}
              sx={{
                p: 1,
                bgcolor: block.id === selectedId ? "rgba(42,245,152,0.08)" : "rgba(255,255,255,0.03)",
                border: "1px solid",
                borderColor: block.id === selectedId ? "rgba(42,245,152,0.45)" : "rgba(255,255,255,0.08)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Chip
                size="small"
                label={TYPE_LABELS[block.type] || block.type}
                sx={{ height: 22, fontSize: 11, bgcolor: "rgba(42,245,152,0.12)", color: "#fff" }}
              />
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 12, flex: 1 }} noWrap>
                {blockSummary(block)}
              </Typography>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); move(index, -1); }} disabled={index === 0} sx={{ color: "rgba(255,255,255,0.7)" }}>
                <ArrowUpwardIcon fontSize="inherit" />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); move(index, 1); }} disabled={index === blocks.length - 1} sx={{ color: "rgba(255,255,255,0.7)" }}>
                <ArrowDownwardIcon fontSize="inherit" />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); duplicate(block); }} sx={{ color: "rgba(255,255,255,0.7)" }}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); remove(block.id); }} sx={{ color: "rgba(255,255,255,0.7)" }}>
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Paper>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {BLOCK_TYPES.map((type) => (
            <Button
              key={type}
              size="small"
              variant="outlined"
              onClick={() => add(type)}
              sx={{ borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.8)", textTransform: "none", fontSize: 12 }}
            >
              + {TYPE_LABELS[type]}
            </Button>
          ))}
        </Box>
        <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
          EDIT BLOCK
        </Typography>
        <Paper sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <BlockForm block={selected} onChange={(next) => updateAt(next.id, next)} />
        </Paper>
      </Box>
      <Box sx={{ flex: { xs: "1 1 auto", md: "1 1 54%" }, minWidth: 0 }}>
        <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
          LIVE PREVIEW
        </Typography>
        <iframe
          title="email-live-preview"
          srcDoc={liveHtml}
          style={{
            width: "100%",
            height: 640,
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            background: "#F8FAFC",
          }}
        />
      </Box>
    </Box>
  );
};

export default BlockEditor;
